// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import { useEffect, useState } from 'react'
import { Route } from 'react-router'
import {
  Conditional,
  MainToolbar,
  RoutedApp,
  useSession,
  useWindowDimensions
} from '../lib'
import logo from '../logo.svg'
import { AccountContext, useAccount } from './console/context'
import * as pages from './console/pages'
import { AccountClient, IAccount } from '../api'
import { AccountSelector, ConsoleMainMenu } from './console/components'
import { getCurrentAccountId, getLastUsedAccountId } from './console/lib'
import { GrantedPermissions } from '../models'


const ConsoleMainToolbar: React.FC = () => {
  const {width} = useWindowDimensions()
  const {accounts, activeAccount, setActiveAccount} = useAccount()
  return (
    <MainToolbar sideMenu={true}>
      <Conditional render={width > 768}>
        <AccountSelector
          accounts={accounts}
          activeAccount={activeAccount}
          setActiveAccount={setActiveAccount}
        />
      </Conditional>
    </MainToolbar>
  )
}

// TODO: Prevent a shitload of API calls.
const client = new AccountClient()
const accountsPromise: Promise<IAccount[]> = client.listAccountsForCurrentUser()


const Application: React.FC<{children: React.ReactNode}> = ({children}) => {
  const currentAccountId = getCurrentAccountId()
  const lastUsedAccountId = getLastUsedAccountId()
  const accountId = currentAccountId || lastUsedAccountId
  const session = useSession()
  const [activeAccount, setActiveAccount] = useState<IAccount | null>(null)
  const [available, setAvailable] = useState<{[key: number]: IAccount} | null>(null)
  const [mustSelectAccount, setMustSelectAccount] = useState<boolean>(accountId === null)
  const [permissions, setPermissions] = useState<GrantedPermissions | null>(null)
  const ctx = {
    accounts: available,
    activeAccount: activeAccount,
    mustSelectAccount: mustSelectAccount,
    permissions: permissions,
    setActiveAccount: (account: IAccount) => setActiveAccount(account)
  }

  useEffect(() => {
    document.title = "Console - Molano Cloud"
  }, [])

  useEffect(() => {
    if (available === null) {
      accountsPromise
        .then((accounts) => {
          setAvailable(
            Object.fromEntries(
              accounts.map((account) => [account.getIdentifier(), account])
            )
          )
        })
        .catch((error: any) => {
          if (error.code !== "AUTHENTICATION_REQUIRED") {
            throw error
          }
          console.log("Service requires fresh access token.")
          setAvailable([])
        })
      return
    }
  }, [available])

  useEffect(() => {
    if (activeAccount === null) {
      return
    }
    console.log("Selected account", activeAccount.getDisplayName())
    activeAccount.getAllPermissions()
      .then(setPermissions)
  }, [activeAccount])

  return (
    <>
      <AccountContext.Provider value={ctx}>{children}</AccountContext.Provider>
    </>
  )
}


const ConsoleApp: React.FC = () => {
  const gettext: (value: string) => string = (value) => value

  return (
    <RoutedApp
      applicationComponent={Application}
      logo={logo}
      mainMenuComponent={ConsoleMainMenu}
      mainToolbarComponent={ConsoleMainToolbar}
      name={gettext("Molano Cloud Console")}
      ready={true}
    >
      <Route exact path="/setup" component={() => (<pages.WizardIndexPage/>)}/>
      <Route exact path="/products/mdm" component={() => (<pages.ProductMasterDataPage/>)}/>
      <Route exact path="/accounts/create" component={() => (<pages.CreateAccount/>)}/>
      <Route exact path="/" component={pages.Dashboard}/>
    </RoutedApp>
  )
}

export default ConsoleApp
